import { concatenateSequences } from "@db/concatenate";
import type { GnomuxSequence } from "@db/content";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCurrentUser } from "@hooks/use-current-user";
import { formatBp } from "@lib/genetics";
import { Button } from "@ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@ui/field";
import { Input } from "@ui/input";
import { Switch } from "@ui/switch";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  type ConcatenateFormData,
  concatenateSchema,
} from "./concatenateSchema";
import { useConcatenateStore } from "./concatenateStore";
import { SortableSequenceItem } from "./SortableSequenceItem";

export function ConcatenateDialog() {
  const { open, sequences, collectionFileId, parent, closeDialog } =
    useConcatenateStore();

  const { user } = useCurrentUser();
  const ownerId = user?.id ?? "local";

  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ConcatenateFormData>({
    resolver: zodResolver(concatenateSchema),
    defaultValues: { name: "", spacer: "", addToCollection: true },
  });

  const spacer = watch("spacer");
  const addToCollection = watch("addToCollection");

  const sequenceMap = useMemo(() => {
    const map = new Map<string, GnomuxSequence>();
    for (const seq of sequences) {
      map.set(seq.id, seq);
    }
    return map;
  }, [sequences]);

  const orderedSequences = useMemo(
    () =>
      orderedIds
        .map((id) => sequenceMap.get(id))
        .filter((s): s is GnomuxSequence => s !== undefined),
    [orderedIds, sequenceMap],
  );

  const totalLength = useMemo(() => {
    const sequenceLength = orderedSequences.reduce(
      (sum, s) => sum + s.length,
      0,
    );
    const spacerLength =
      spacer.length > 0 ? spacer.length * (orderedSequences.length - 1) : 0;
    return sequenceLength + spacerLength;
  }, [orderedSequences, spacer]);

  const kind = sequences.length > 0 ? sequences[0].kind : "nt";

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedIds((ids) => {
        const oldIndex = ids.indexOf(active.id as string);
        const newIndex = ids.indexOf(over.id as string);
        return arrayMove(ids, oldIndex, newIndex);
      });
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      closeDialog();
    }
  }

  const handleOpen = useCallback(() => {
    setOrderedIds(sequences.map((s) => s.id));
    reset({ name: "", spacer: "", addToCollection: true });
  }, [sequences, reset]);

  async function onSubmit(data: ConcatenateFormData) {
    await concatenateSequences({
      name: data.name.trim(),
      sequenceIds: orderedIds,
      spacer: data.spacer,
      ownerId,
      parent,
      collectionFileId: data.addToCollection ? collectionFileId : undefined,
    });
    closeDialog();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" onOpenAutoFocus={handleOpen}>
        <DialogHeader>
          <DialogTitle>Concatenate Sequences</DialogTitle>
          <DialogDescription>
            Join {sequences.length} sequences end-to-end into a single new
            sequence.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="concat-name">Name</FieldLabel>
              <Input
                id="concat-name"
                placeholder="Concatenated sequence name"
                {...register("name")}
              />
              <FieldError errors={[errors.name]} />
            </Field>

            <Field>
              <FieldLabel htmlFor="concat-spacer">Spacer</FieldLabel>
              <Input
                id="concat-spacer"
                placeholder={
                  kind === "nt"
                    ? "e.g. NNNNNN (optional)"
                    : "e.g. XXX (optional)"
                }
                className="font-mono"
                {...register("spacer", {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue("spacer", e.target.value.toUpperCase());
                  },
                })}
              />
              <FieldDescription>
                Inserted between each pair of sequences.
              </FieldDescription>
            </Field>

            {collectionFileId && (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="concat-add-to-collection">
                  Add to collection
                </FieldLabel>
                <Switch
                  id="concat-add-to-collection"
                  checked={addToCollection}
                  onCheckedChange={(checked) =>
                    setValue("addToCollection", checked === true)
                  }
                />
              </Field>
            )}

            <div className="flex flex-col gap-2">
              <FieldLabel>Order</FieldLabel>
              <FieldDescription>
                Drag to reorder. Sequences are joined top to bottom.
              </FieldDescription>
              <div className="max-h-64 overflow-hidden overflow-y-auto rounded-md border p-2">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={orderedIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedSequences.map((seq) => (
                      <SortableSequenceItem key={seq.id} sequence={seq} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>

            <p className="text-muted-foreground text-sm">
              Result: {formatBp(totalLength)}
            </p>
          </FieldGroup>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Concatenating..." : "Concatenate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
